import type { AxiosInstance } from "axios";
import type { QueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  CompanyEnrichmentRequestDetailViewSchema,
  CompanyEnrichmentRequestRowSchema,
  CompanyEnrichmentTriggerResultSchema,
  type CompanyEnrichmentTriggerResult,
} from "@shared/schemas/company-enrichment-request-admin.schemas";
import { EnrichedCompanyViewSchema } from "@shared/schemas/enriched-company-admin.schemas";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import {
  createCompanyEnrichmentAdminApi,
  type CompanyEnrichmentRequestsQuery,
} from "./company-enrichment-admin.api";

const REQUESTS_STALE_TIME_MS = 30_000;

// The list endpoint returns a paginated envelope; parsing it here (rather than
// in the api client) is the repository's DTO→domain boundary, and z.coerce.date
// turns wire ISO strings into the Date objects the components rely on.
const RequestsPageSchema = z.object({
  items: z.array(CompanyEnrichmentRequestRowSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});

export interface TriggerCompanyEnrichmentVariables {
  readonly companyEnrichmentRequestIdsPublic: readonly string[];
}

const QUERY_KEYS = {
  requests: (query: CompanyEnrichmentRequestsQuery) =>
    [
      "company-enrichment-admin",
      "requests",
      query.page ?? null,
      query.limit ?? null,
    ] as const,
  requestsRoot: () => ["company-enrichment-admin", "requests"] as const,
  request: (idPublic: string) =>
    ["company-enrichment-admin", "request", idPublic] as const,
  enrichedCompany: (idPublic: string) =>
    ["company-enrichment-admin", "enriched-company", idPublic] as const,
};

export class CompanyEnrichmentAdminHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createCompanyEnrichmentAdminApi(apiClient);
  }

  createHooks() {
    const { api } = this;
    return {
      useGetCompanyEnrichmentRequests: (
        query: CompanyEnrichmentRequestsQuery = {},
      ) =>
        this.query({
          key: QUERY_KEYS.requests(query),
          fn: () => api.getRequests(query),
          parse: (dto) => RequestsPageSchema.parse(dto),
          staleTime: REQUESTS_STALE_TIME_MS,
        }),

      useGetCompanyEnrichmentRequest: (idPublic: string) =>
        this.query({
          key: QUERY_KEYS.request(idPublic),
          fn: () => api.getRequest(idPublic),
          parse: (dto) => CompanyEnrichmentRequestDetailViewSchema.parse(dto),
          enabled: idPublic !== "",
          staleTime: REQUESTS_STALE_TIME_MS,
        }),

      useGetEnrichedCompany: (idPublic: string) =>
        this.query({
          key: QUERY_KEYS.enrichedCompany(idPublic),
          fn: () => api.getEnrichedData(idPublic),
          parse: (dto) => EnrichedCompanyViewSchema.parse(dto),
          enabled: idPublic !== "",
          staleTime: REQUESTS_STALE_TIME_MS,
        }),

      useTriggerCompanyEnrichment: () =>
        this.mutation<
          readonly unknown[],
          readonly CompanyEnrichmentTriggerResult[],
          TriggerCompanyEnrichmentVariables
        >({
          fn: ({ companyEnrichmentRequestIdsPublic }) =>
            this.api.trigger(companyEnrichmentRequestIdsPublic),
          parse: (dtos) =>
            dtos.map((dto) => CompanyEnrichmentTriggerResultSchema.parse(dto)),
          onSuccess: () => {
            this.invalidateQueries(QUERY_KEYS.requestsRoot());
          },
        }),
    };
  }
}
